/**
 * Iter116 — No-Website Lead Hub.
 *
 * Find local businesses that DON'T have a website on OpenStreetMap.
 * Each is a high-conversion target for a digital agency.
 * Source: Overpass API (free, anonymous). Server cost: $0.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, MapPin, Phone, Sparkles, Copy, Search,
  AlertTriangle, Globe, Wand2, Languages, Paperclip, X, Trash2,
  ShieldCheck, Facebook, Instagram, CheckCircle2, XCircle, HelpCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = {
  Orlando:   [28.40, -81.55, 28.65, -81.20],
  Miami:     [25.70, -80.45, 25.90, -80.10],
  Tampa:     [27.85, -82.65, 28.05, -82.30],
  Jacksonville: [30.20, -81.85, 30.50, -81.45],
};

export default function NoWebsiteLeads() {
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [lastReport, setLastReport] = useState(null);
  const [bboxPreset, setBboxPreset] = useState("Orlando");
  const [limit, setLimit] = useState(100);
  const [activeOutreach, setActiveOutreach] = useState(null);
  const [verifyingIds, setVerifyingIds] = useState({});
  const [verifyJob, setVerifyJob] = useState(null);
  const [verFilter, setVerFilter] = useState("");
  const [sortMode, setSortMode] = useState("recent");
  const [approachLead, setApproachLead] = useState(null);

  const loadStats = async () => {
    try {
      const { data } = await axios.get(`${API}/no-website-leads/stats`);
      setStats(data);
    } catch (_e) {}
  };
  const loadLeads = async (vf, sm) => {
    try {
      const params = { has_phone: true, limit: 200, sort: sm !== undefined ? sm : sortMode };
      const f = vf !== undefined ? vf : verFilter;
      if (f === "not_a_lead") { params.verification = "NOT_A_LEAD"; params.include_not_leads = true; }
      else if (f) params.verification = f;
      const { data } = await axios.get(`${API}/no-website-leads`, { params });
      setLeads(data.leads || []);
    } catch (_e) {}
  };
  useEffect(() => { loadStats(); loadLeads(); }, []);
  useEffect(() => { loadLeads(verFilter, sortMode); }, [verFilter, sortMode]); // eslint-disable-line

  const verifyLead = async (lead) => {
    setVerifyingIds((v) => ({ ...v, [lead.id]: true }));
    try {
      const { data } = await axios.post(`${API}/no-website-leads/${lead.id}/verify`);
      const st = data.verification?.status;
      toast.success(
        st === "REAL_OPPORTUNITY" ? "🟢 Oportunidade real confirmada!"
          : st === "NEED_REVIEW" ? "🟡 Encontrou presença parcial — revisar"
          : "🔴 Website encontrado — removido da lista");
      loadLeads(); loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na verificação");
    } finally {
      setVerifyingIds((v) => ({ ...v, [lead.id]: false }));
    }
  };

  const runBulkVerify = async () => {
    try {
      const { data: job } = await axios.post(`${API}/no-website-leads/verify-bulk`, {
        limit: 25, only_unverified: true,
      });
      if (!job.total) { toast.info("Todos os leads já foram verificados"); return; }
      setVerifyJob({ ...job, done: 0 });
      toast.success(`Verificando ${job.total} leads em segundo plano...`);
      const poll = setInterval(async () => {
        try {
          const { data: st } = await axios.get(`${API}/no-website-leads/verify-jobs/${job.job_id}`);
          setVerifyJob(st);
          if (st.status === "done") {
            clearInterval(poll);
            const c = st.counts || {};
            toast.success(
              `Verificação concluída · 🟢 ${c.REAL_OPPORTUNITY || 0} reais · 🟡 ${c.NEED_REVIEW || 0} revisar · 🔴 ${c.NOT_A_LEAD || 0} removidos`);
            loadLeads(); loadStats();
            setTimeout(() => setVerifyJob(null), 8000);
          }
        } catch (_e) { clearInterval(poll); }
      }, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao iniciar verificação");
    }
  };

  const runScan = async () => {
    setScanning(true); setLastReport(null);
    try {
      const { data } = await axios.post(`${API}/no-website-leads/scan`, {
        bbox: PRESETS[bboxPreset], require_phone: true, limit,
      });
      setLastReport(data);
      toast.success(`${data.inserted} novos leads capturados`);
      loadStats(); loadLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no scan");
    } finally { setScanning(false); }
  };

  const prepareOutreach = async (lead, kind = "whatsapp") => {
    try {
      const { data } = await axios.post(
        `${API}/no-website-leads/${lead.id}/prepare-outreach`,
        { template_kind: kind, language: "pt" },
      );
      setActiveOutreach({ lead, ...data });
    } catch (e) {
      toast.error("Falha ao preparar mensagem");
    }
  };

  return (
    <div className="al-shell" data-testid="no-website-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <header className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold text-[var(--al-text)]">
                AI Business Opportunity Finder
              </h1>
              <span className="px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-400 text-[10px] font-bold uppercase tracking-wide">
                $0 / mês
              </span>
            </div>
            <p className="text-[var(--al-text-muted)] text-sm mt-1">
              Empresas <strong>perdendo clientes online</strong> — sem website,
              site ruim ou redes sociais abandonadas. Captura via{" "}
              <strong>OpenStreetMap</strong> + validação cruzada com IA.
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <button
              data-testid="nwl-verify-bulk-btn"
              onClick={runBulkVerify}
              disabled={!!verifyJob && verifyJob.status !== "done"}
              title="Validação cruzada: busca website, Facebook e Instagram antes de classificar"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-[13px] font-semibold disabled:opacity-60"
            >
              {verifyJob && verifyJob.status !== "done"
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <ShieldCheck className="w-4 h-4" />}
              {verifyJob && verifyJob.status !== "done"
                ? `${verifyJob.done || 0}/${verifyJob.total}`
                : "Verificar leads"}
            </button>
            <select
              value={bboxPreset}
              onChange={(e) => setBboxPreset(e.target.value)}
              className="al-input h-10 text-[12.5px]"
              data-testid="nwl-bbox-preset"
            >
              {Object.keys(PRESETS).map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="al-input h-10 text-[12.5px]"
              data-testid="nwl-limit"
            >
              {[50, 100, 200, 300].map((n) => (
                <option key={n} value={n}>{n} leads</option>
              ))}
            </select>
            <button
              data-testid="nwl-scan-btn"
              onClick={runScan}
              disabled={scanning}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[13px] font-semibold disabled:opacity-50"
            >
              {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Buscar
            </button>
          </div>
        </header>

        <div className="flex gap-2 border-b border-[var(--al-border)] mb-5">
          {["dashboard", "leads"].map((id) => (
            <button
              key={id}
              data-testid={`nwl-tab-${id}`}
              onClick={() => setTab(id)}
              className={`px-4 py-2 text-[13px] font-semibold transition border-b-2 ${
                tab === id
                  ? "text-[var(--al-text)] border-[#3B82F6]"
                  : "text-[var(--al-text-muted)] border-transparent hover:text-[var(--al-text)]"
              }`}
            >
              {id === "dashboard" ? "Dashboard" : "Leads (com telefone)"}
            </button>
          ))}
        </div>

        {tab === "dashboard" && stats && (
          <Dashboard stats={stats} lastReport={lastReport} />
        )}

        {tab === "leads" && (
          <>
            <div className="flex items-center gap-2 mb-3">
              <select
                data-testid="nwl-ver-filter"
                value={verFilter}
                onChange={(e) => setVerFilter(e.target.value)}
                className="al-input h-9 text-[12.5px] min-w-[220px]"
              >
                <option value="">Verificação: ativos (sem 🔴)</option>
                <option value="REAL_OPPORTUNITY">🟢 Real Opportunity</option>
                <option value="WEBSITE_UPGRADE">🔧 Website Upgrade</option>
                <option value="NEED_REVIEW">🟡 Need Review</option>
                <option value="unverified">⏳ Não verificados</option>
                <option value="not_a_lead">🔴 Not a Lead (removidos)</option>
              </select>
              <select
                data-testid="nwl-sort-select"
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value)}
                className="al-input h-9 text-[12.5px]"
              >
                <option value="recent">Mais recentes</option>
                <option value="score">🔥 Maior Opportunity Score</option>
              </select>
              <span className="text-[11px] text-[var(--al-text-muted)]">
                {leads.length} leads
              </span>
            </div>
            <LeadsList leads={leads} onPrepare={prepareOutreach}
              onVerify={verifyLead} verifyingIds={verifyingIds}
              onApproach={(l) => setApproachLead(l)} />
          </>
        )}

        {approachLead && (
          <ApproachModal lead={approachLead} onClose={() => setApproachLead(null)} />
        )}

        {activeOutreach && (
          <OutreachModal
            data={activeOutreach}
            onClose={() => setActiveOutreach(null)}
          />
        )}
      </div>
    </div>
  );
}

function Dashboard({ stats, lastReport }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Stat title="Total capturado" value={stats.total} accent="text-[var(--al-text)]" />
        <Stat title="Com telefone" value={stats.with_phone} accent="text-emerald-400" />
        <Stat title="Capturados hoje" value={stats.captured_today} accent="text-[#3B82F6]" />
        <Stat title="Outreach disparados" value={stats.outreach_triggered} accent="text-[#F59E0B]" />
      </div>
      <div className="rounded-xl border border-[var(--al-border)] bg-[var(--al-row-alt)] p-4">
        <div className="text-[var(--al-text-muted)] text-[11px] uppercase tracking-wide mb-3">
          Top categorias (sem site)
        </div>
        <div className="space-y-2">
          {stats.top_categories.map((c) => (
            <div key={c.category} className="flex items-center gap-3">
              <div className="text-[12.5px] text-[var(--al-text)] capitalize w-32">
                {c.category.replace(/_/g, " ")}
              </div>
              <div className="flex-1 h-2.5 bg-[var(--al-border)] rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500"
                  style={{ width: `${Math.min(100, (c.count / stats.total) * 100)}%` }}
                />
              </div>
              <div className="text-[11px] text-[var(--al-text-muted)] w-10 text-right">{c.count}</div>
            </div>
          ))}
        </div>
      </div>
      {lastReport && (
        <div className="rounded-xl border border-[var(--al-border)] bg-[var(--al-row-alt)] p-4 text-[12px] text-[var(--al-text-muted)]">
          <div className="font-semibold text-[var(--al-text)] mb-1">Último scan</div>
          {lastReport.raw_elements} pontos no OSM · {lastReport.valid_candidates}{" "}
          candidatos válidos · {lastReport.inserted} novos · {lastReport.skipped_already_captured}{" "}
          já existentes.
        </div>
      )}
    </div>
  );
}

function Stat({ title, value, accent }) {
  return (
    <div className="rounded-xl border border-[var(--al-border)] bg-[var(--al-row-alt)] p-4">
      <div className="text-[var(--al-text-muted)] text-[10.5px] uppercase tracking-wide mb-1">{title}</div>
      <div className={`text-3xl font-bold ${accent}`}>{value}</div>
    </div>
  );
}

function LeadsList({ leads, onPrepare, onVerify, verifyingIds, onApproach }) {
  if (!leads.length) {
    return (
      <div className="rounded-xl border border-[var(--al-border)] bg-[var(--al-row-alt)] p-10 text-center text-[var(--al-text-muted)]">
        Nenhum lead ainda. Selecione uma cidade acima e clique em "Buscar".
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {leads.map((l) => (
        <div
          key={l.id}
          data-testid={`nwl-lead-${l.id}`}
          className="rounded-xl border border-[var(--al-border)] bg-[var(--al-row-alt)] p-4 hover:border-[#3B82F6]/40 transition"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-bold text-[var(--al-text)] text-[14px] mb-0.5">
                {l.company_name}
              </div>
              <div className="text-[11.5px] text-[var(--al-text-muted)] capitalize mb-2">
                {(l.category || "").replace(/_/g, " ")} · {l.city || "—"}
              </div>
              {l.phone && (
                <div className="flex items-center gap-1.5 text-[12px] text-[var(--al-text)] mb-1">
                  <Phone className="w-3 h-3" /> {l.phone}
                </div>
              )}
              {l.address && (
                <div className="flex items-center gap-1.5 text-[11px] text-[var(--al-text-muted)]">
                  <MapPin className="w-3 h-3" /> {l.address}
                </div>
              )}
              <div className="flex flex-wrap gap-1 mt-2">
                {(l.missing_signals || []).map((m) => (
                  <span
                    key={m}
                    className="px-1.5 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B] text-[9.5px] font-semibold uppercase tracking-wider"
                  >
                    sem {m === "opening_hours" ? "horário" : m} (OSM)
                  </span>
                ))}
              </div>
              <DigitalPresence lead={l} />
            </div>
            <div className="shrink-0 flex flex-col gap-1.5 items-stretch">
              <button
                data-testid={`nwl-verify-${l.id}`}
                onClick={() => onVerify(l)}
                disabled={!!verifyingIds[l.id]}
                title="Validação cruzada (website + Facebook + Instagram)"
                className="inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-[11px] font-semibold disabled:opacity-60"
              >
                {verifyingIds[l.id]
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <ShieldCheck className="w-3 h-3" />}
                {verifyingIds[l.id] ? "Escaneando..." : "Deep Scan"}
              </button>
              <button
                data-testid={`nwl-approach-${l.id}`}
                onClick={() => onApproach(l)}
                title="Gerar abordagem completa com IA (análise + WhatsApp + email + ligação)"
                className="inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#F59E0B] hover:bg-[#D97706] text-white text-[11px] font-semibold"
              >
                <Wand2 className="w-3 h-3" /> Abordagem IA
              </button>
              <button
                data-testid={`nwl-prepare-${l.id}`}
                onClick={() => onPrepare(l, "whatsapp")}
                className="inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold"
              >
                <Sparkles className="w-3 h-3" /> Preparar
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DigitalPresence({ lead }) {
  const v = lead.digital_verification;
  if (!v) {
    return (
      <div data-testid={`nwl-dp-${lead.id}`} className="mt-2 flex items-center gap-1.5 text-[10.5px] text-[var(--al-text-muted)] italic">
        <HelpCircle className="w-3 h-3" />
        Presença digital não verificada — o OSM sozinho não confirma ausência.
      </div>
    );
  }
  const st = v.status;
  const badge = st === "REAL_OPPORTUNITY"
    ? { label: "🔥 HIGH VALUE DIGITAL LEAD", cls: "bg-emerald-500/15 text-emerald-500" }
    : st === "WEBSITE_UPGRADE"
      ? { label: "🔧 WEBSITE UPGRADE OPPORTUNITY", cls: "bg-blue-500/15 text-blue-400" }
      : st === "NEED_REVIEW"
        ? { label: "🟡 NEED REVIEW", cls: "bg-amber-500/15 text-amber-500" }
        : { label: "🔴 NOT A LEAD", cls: "bg-red-500/15 text-red-500" };
  const q = v.website_quality;
  const score = v.opportunity_score ?? lead.opportunity_score;
  const oClass = v.opportunity_class ?? lead.opportunity_class;
  const classBadge = oClass === "HOT"
    ? { label: `🔥 HOT ${score}`, cls: "bg-red-500/15 text-red-400" }
    : oClass === "WARM"
      ? { label: `⚡ WARM ${score}`, cls: "bg-amber-500/15 text-amber-500" }
      : { label: `LOW ${score}`, cls: "bg-gray-500/15 text-gray-400" };
  const Row = ({ icon: Icon, label, check }) => (
    <div className="flex items-center gap-1.5 text-[10.5px]">
      <Icon className="w-3 h-3 text-[var(--al-text-muted)]" />
      <span className="text-[var(--al-text-muted)] w-16">{label}</span>
      {check?.status === "found" ? (
        <span className="inline-flex items-center gap-1.5 min-w-0">
          <a href={check.url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[#3B82F6] hover:underline font-semibold truncate max-w-[170px]">
            <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
            {(check.url || "").replace(/^https?:\/\/(www\.)?/, "").slice(0, 30)}
          </a>
          <span className={`text-[8.5px] font-bold px-1 py-0.5 rounded shrink-0 ${
            check.match_label === "found_verified" ? "bg-emerald-500/15 text-emerald-500"
              : check.match_label === "likely_match" ? "bg-amber-500/15 text-amber-500"
                : "bg-orange-500/15 text-orange-400"}`}>
            {check.match_label === "found_verified" ? "🟢 Verified"
              : check.match_label === "likely_match" ? "🟡 Possible"
                : "🟠 Review"}{check.match ? ` ${check.match}%` : ""}
          </span>
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-[var(--al-text-muted)] font-semibold">
          <HelpCircle className="w-3 h-3" /> ⚪ Not found yet
          {check?.sources_checked ? ` (${check.sources_checked} fontes)` : ""}
        </span>
      )}
    </div>
  );
  const ScoreChip = ({ label, value }) => value == null ? null : (
    <span className={`text-[9.5px] font-bold px-1.5 py-0.5 rounded ${
      value < 50 ? "bg-red-500/15 text-red-400"
        : value < 80 ? "bg-amber-500/15 text-amber-500"
          : "bg-emerald-500/15 text-emerald-500"}`}>
      {label} {value}/100
    </span>
  );
  return (
    <div data-testid={`nwl-dp-${lead.id}`} className="mt-2.5 rounded-lg border border-[var(--al-border)] bg-[var(--al-bg)] p-2.5 space-y-1.5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[9.5px] uppercase tracking-wider font-bold text-[var(--al-text-muted)]">
          Digital Analysis
        </span>
        <div className="flex items-center gap-1.5">
          {score != null && (
            <span data-testid={`nwl-oscore-${lead.id}`} className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${classBadge.cls}`}>
              {classBadge.label}
            </span>
          )}
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${badge.cls}`}>
            {badge.label}
          </span>
        </div>
      </div>
      <Row icon={Globe} label="Website" check={v.website} />
      <Row icon={Facebook} label="Facebook" check={v.facebook} />
      <Row icon={Instagram} label="Instagram" check={v.instagram} />
      {v.linkedin?.status === "found" && (
        <Row icon={Globe} label="LinkedIn" check={v.linkedin} />
      )}
      {v.google_business?.status === "found" && (
        <div className="flex items-center gap-1.5 text-[10.5px]">
          <MapPin className="w-3 h-3 text-[var(--al-text-muted)]" />
          <span className="text-[var(--al-text-muted)] w-16">G. Business</span>
          <a href={v.google_business.url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[#3B82F6] hover:underline font-semibold">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            Perfil no Maps{v.google_business.rating ? ` · ★${v.google_business.rating} (${v.google_business.ratings_total})` : ""}
          </a>
        </div>
      )}
      {q && q.reachable && (
        <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
          <ScoreChip label="Mobile" value={q.mobile_performance} />
          <ScoreChip label="SEO" value={q.seo_score} />
          <span className={`text-[9.5px] font-bold px-1.5 py-0.5 rounded ${q.ssl_ok ? "bg-emerald-500/15 text-emerald-500" : "bg-red-500/15 text-red-400"}`}>
            {q.ssl_ok ? "SSL OK" : "SEM SSL ⚠️"}
          </span>
          {q.technology && (
            <span className="text-[9.5px] font-semibold px-1.5 py-0.5 rounded bg-gray-500/15 text-gray-400">
              {q.technology}{q.tech_version ? ` ${q.tech_version}` : ""}
              {q.age_risk === "high" ? " · antigo ⚠️" : ""}
            </span>
          )}
        </div>
      )}
      <div className="text-[9.5px] text-[var(--al-text-muted)] pt-0.5">
        Confidence: <strong className="text-[var(--al-text)]">{v.confidence}%</strong>
        {v.sources_checked ? <> {" · "}Sources: <strong className="text-[var(--al-text)]">{v.sources_checked.length}/{v.sources_available || 6}</strong></> : null}
        {" · "}Engines: {(v.engines_used || []).join(", ")}
        {" · "}{new Date(v.verified_at).toLocaleDateString()}
      </div>
    </div>
  );
}

const APPROACH_TABS = [
  { id: "problem_analysis", label: "📊 Análise" },
  { id: "improvement_suggestion", label: "💡 Proposta" },
  { id: "whatsapp_message", label: "💬 WhatsApp" },
  { id: "cold_email", label: "✉️ Email" },
  { id: "call_script", label: "📞 Ligação" },
];

function ApproachModal({ lead, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(lead.last_approach || null);
  const [tab, setTab] = useState("problem_analysis");

  const generate = async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.post(
        `${API}/no-website-leads/${lead.id}/prepare-approach`, { language: "pt" });
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar abordagem");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (lead.last_approach) { setData(lead.last_approach); setLoading(false); }
    else generate();
  }, [lead.id]); // eslint-disable-line

  const copyTab = () => {
    navigator.clipboard.writeText((data && data[tab]) || "");
    toast.success("Copiado para o clipboard");
  };
  const waLink = lead.phone && data?.whatsapp_message
    ? `https://wa.me/${lead.phone.replace(/\D/g, "")}?text=${encodeURIComponent(data.whatsapp_message)}`
    : null;

  return (
    <div data-testid="nwl-approach-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="al-card max-w-[680px] w-full p-6 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-[16px] font-bold text-[var(--al-text)] flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-[#F59E0B]" /> Abordagem IA — {lead.company_name}
            </h2>
            <p className="text-[11px] text-[var(--al-text-muted)] mt-0.5">
              Gerada com base no diagnóstico digital real do lead.
            </p>
          </div>
          <button onClick={onClose} className="text-[var(--al-text-muted)] hover:text-[var(--al-text)]" data-testid="nwl-approach-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center gap-3 py-14 text-[12px] text-[var(--al-text-muted)]">
            <Loader2 className="w-6 h-6 animate-spin text-[#F59E0B]" />
            Analisando presença digital e escrevendo a abordagem...
          </div>
        ) : data ? (
          <>
            <div className="flex gap-1 border-b border-[var(--al-border)] mb-3 overflow-x-auto">
              {APPROACH_TABS.map((tb) => (
                <button key={tb.id} data-testid={`nwl-approach-tab-${tb.id}`}
                  onClick={() => setTab(tb.id)}
                  className={`px-3 py-2 text-[11.5px] font-semibold whitespace-nowrap border-b-2 ${
                    tab === tb.id ? "text-[var(--al-text)] border-[#F59E0B]"
                      : "text-[var(--al-text-muted)] border-transparent hover:text-[var(--al-text)]"}`}>
                  {tb.label}
                </button>
              ))}
            </div>
            <div data-testid="nwl-approach-content" className="flex-1 overflow-y-auto rounded-lg border border-[var(--al-border)] bg-[var(--al-bg)] p-3 text-[12.5px] text-[var(--al-text)] whitespace-pre-wrap min-h-[160px]">
              {data[tab] || "—"}
            </div>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <button data-testid="nwl-approach-copy" onClick={copyTab}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[var(--al-border)] text-[12px] font-semibold text-[var(--al-text)]">
                <Copy className="w-3.5 h-3.5" /> Copiar
              </button>
              {tab === "whatsapp_message" && waLink && (
                <a href={waLink} target="_blank" rel="noopener noreferrer"
                  data-testid="nwl-approach-wa"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-white text-[12px] font-semibold"
                  style={{ background: "#25D366" }}>
                  <Phone className="w-3.5 h-3.5" /> Abrir WhatsApp
                </a>
              )}
              <button data-testid="nwl-approach-regen" onClick={generate}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#F59E0B]/50 text-[12px] font-semibold text-[#F59E0B]">
                <Wand2 className="w-3.5 h-3.5" /> Gerar novamente
              </button>
            </div>
          </>
        ) : (
          <div className="py-10 text-center text-[12px] text-[var(--al-text-muted)]">
            Falha ao gerar. <button onClick={generate} className="text-[#F59E0B] font-semibold">Tentar de novo</button>
          </div>
        )}
      </div>
    </div>
  );
}

function OutreachModal({ data, onClose }) {
  const [text, setText] = useState(data.rendered_text || "");
  const [busy, setBusy] = useState(null);   // "improve" | "translate-pt" | "translate-en" | "translate-es" | "upload"
  const [attachments, setAttachments] = useState(data.attachments || []);

  const copy = () => {
    navigator.clipboard.writeText(text);
    toast.success("Texto copiado para o clipboard");
  };
  const waLink = data.recipient_phone
    ? `https://wa.me/${data.recipient_phone.replace(/\D/g, "")}?text=${encodeURIComponent(text)}`
    : null;

  const runAi = async (action, lang) => {
    const key = action === "improve" ? "improve" : `translate-${lang}`;
    setBusy(key);
    try {
      const { data: r } = await axios.post(`${API}/no-website-leads/ai-text`, {
        text,
        action,
        target_language: lang,
      });
      setText(r.text || text);
      toast.success(action === "improve" ? "Texto melhorado pela IA" : `Traduzido para ${lang.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na IA");
    } finally { setBusy(null); }
  };

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Arquivo grande demais (máx 2MB)");
      return;
    }
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: r } = await axios.post(
        `${API}/no-website-leads/${data.lead_id}/attachments`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setAttachments([...attachments, r.attachment]);
      toast.success(`${f.name} anexado`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no upload");
    } finally {
      setBusy(null);
      e.target.value = "";   // reset so re-upload of same file works
    }
  };

  const removeAttachment = async (assetId) => {
    try {
      await axios.delete(
        `${API}/no-website-leads/${data.lead_id}/attachments/${assetId}`,
      );
      setAttachments(attachments.filter((a) => a.id !== assetId));
    } catch (_e) { toast.error("Falha ao remover"); }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="nwl-outreach-modal"
        className="w-full max-w-2xl rounded-2xl border border-[var(--al-border)] bg-[var(--al-row)] p-6 space-y-4 max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="font-bold text-[var(--al-text)] text-[16px]">
              {data.lead.company_name}
            </div>
            <div className="text-[11px] text-[var(--al-text-muted)]">
              Template: <strong>{data.template_name}</strong> · {data.kind} · {data.language}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--al-text-muted)] hover:text-[var(--al-text)] text-xl leading-none"
            data-testid="nwl-outreach-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* IA + tradução toolbar */}
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            data-testid="nwl-ai-improve"
            onClick={() => runAi("improve", "pt")}
            disabled={busy !== null}
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md bg-purple-600/15 hover:bg-purple-600 text-purple-300 hover:text-white text-[11px] font-semibold disabled:opacity-50 transition"
            title="Melhorar texto via IA"
          >
            {busy === "improve" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
            Melhorar
          </button>
          <span className="inline-flex items-center gap-1 text-[10px] text-[var(--al-text-muted)] uppercase tracking-wide ml-1">
            <Languages className="w-3 h-3" /> Traduzir:
          </span>
          {[
            ["pt", "🇧🇷 PT"],
            ["en", "🇺🇸 EN"],
            ["es", "🇪🇸 ES"],
          ].map(([lang, label]) => (
            <button
              key={lang}
              data-testid={`nwl-translate-${lang}`}
              onClick={() => runAi("translate", lang)}
              disabled={busy !== null}
              className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md bg-[var(--al-row-alt)] hover:bg-[#3B82F6] text-[var(--al-text)] hover:text-white text-[11px] font-semibold border border-[var(--al-border)] disabled:opacity-50 transition"
            >
              {busy === `translate-${lang}` ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              {label}
            </button>
          ))}
          <label
            data-testid="nwl-attach-label"
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md bg-amber-500/15 hover:bg-amber-500 text-amber-400 hover:text-white text-[11px] font-semibold cursor-pointer ml-auto transition"
            title="Anexar imagem ou PDF (≤2MB)"
          >
            {busy === "upload" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
            Anexar
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={uploadFile}
              data-testid="nwl-attach-input"
              className="hidden"
            />
          </label>
        </div>

        {/* Editable text area */}
        <textarea
          data-testid="nwl-outreach-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          className="al-input w-full text-[12.5px] text-[var(--al-text)] leading-relaxed font-sans"
        />

        {/* Attachments list */}
        {attachments.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold">
              Anexos ({attachments.length})
            </div>
            {attachments.map((a) => (
              <div
                key={a.id}
                data-testid={`nwl-attach-${a.id}`}
                className="flex items-center gap-2 rounded-md border border-[var(--al-border)] bg-[var(--al-row-alt)] p-2 text-[11.5px]"
              >
                <Paperclip className="w-3 h-3 text-[var(--al-text-muted)]" />
                <span className="text-[var(--al-text)] truncate flex-1">{a.filename}</span>
                <span className="text-[var(--al-text-muted)] text-[10px]">
                  {Math.round(a.size / 1024)} KB · {a.content_type}
                </span>
                <button
                  onClick={() => removeAttachment(a.id)}
                  className="text-[var(--al-text-muted)] hover:text-[#DC2626]"
                  data-testid={`nwl-attach-remove-${a.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            <p className="text-[10px] text-[var(--al-text-muted)] italic">
              Os anexos ficam salvos no lead. Envie manualmente pelo WhatsApp Web depois de abrir o link.
            </p>
          </div>
        )}

        <div className="flex gap-2 flex-wrap">
          <button
            onClick={copy}
            data-testid="nwl-outreach-copy"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px] font-semibold"
          >
            <Copy className="w-3.5 h-3.5" /> Copiar texto
          </button>
          {waLink && (
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nwl-outreach-wa"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-semibold"
            >
              Abrir no WhatsApp
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
